/**
 * Public satisFINDER revision page — lets a mystery shopper edit her previously
 * submitted FIND and resubmit it back to QA. Reached via a public link
 * (no login required) — the `publicId` URL segment IS the bearer secret.
 *
 * Route: /survey/revise/:publicId   (no auth)
 *
 * Designed as a single scroll-down form. Each question type has a
 * pre-populated editor. Photos, voice recordings, and selections are all
 * preserved when she submits — only the fields she touched will change.
 */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertTriangle, CheckCircle2, Clock, Play, Pause, Volume2,
  Star, Trash2, RotateCcw, FileText, Image as ImageIcon,
  Hash, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { FileDropzone } from '@/components/FileDropzone';
import { cleanQuestionLabel, getQuestionType, getQuestionLabel, DISPLAY_ONLY_TYPES } from '@/lib/answerFormatting';

function toAbsUrl(u) {
  const base = process.env.REACT_APP_BACKEND_URL || '';
  if (!u) return '';
  if (/^https?:/i.test(u)) return u;
  if (u.startsWith('/api/')) return base + u;
  return u;
}

function AudioPlayer({ url }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const resolvedUrl = toAbsUrl(url);
  const toggle = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(resolvedUrl);
      audioRef.current.onended = () => setPlaying(false);
      audioRef.current.onerror = () => { setPlaying(false); toast.error('Could not load audio'); };
    }
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };
  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);
  return (
    <button
      type="button"
      onClick={toggle}
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium bg-[#D62B27]/10 text-[#D62B27] hover:bg-[#D62B27]/20"
    >
      {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      {playing ? 'Pause' : 'Play original voice answer'}
    </button>
  );
}

function ResolveOptions(q) {
  const qtype = getQuestionType(q);
  if (qtype === 'yes_no') return [{ label: 'Yes' }, { label: 'No' }, { label: 'N/A' }];
  const opts = q.options || q.choices || [];
  return (Array.isArray(opts) ? opts : []).map(o => {
    if (typeof o === 'string') return { label: o };
    if (o && typeof o === 'object') return { label: o.label || o.text || String(o.value ?? '') };
    return null;
  }).filter(Boolean);
}

function selectedLabelsFromRaw(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.map(x => (x && typeof x === 'object') ? (x.label || x.text || String(x.value ?? '')) : String(x)).filter(Boolean);
  if (typeof raw === 'object') {
    if (Array.isArray(raw.value)) return raw.value.map(x => (x && typeof x === 'object') ? (x.label || x.text || String(x.value ?? '')) : String(x)).filter(Boolean);
    return [raw.label || (raw.value != null ? String(raw.value) : '')].filter(Boolean);
  }
  return [String(raw)].filter(Boolean);
}

function buildChoiceAnswer(qtype, labels) {
  if (qtype === 'yes_no') {
    const v = labels[0];
    if (!v) return null;
    const lower = String(v).toLowerCase();
    const map = { yes: { value: 'yes', score_value: 1 }, no: { value: 'no', score_value: 0 }, 'n/a': { value: 'n/a', score_value: null } };
    return { label: v, ...(map[lower] || { value: lower }) };
  }
  if (qtype === 'multiple_choice') {
    return labels.map(label => ({ label }));
  }
  return { label: labels[0] || null };
}

function ratingFromRaw(raw) {
  if (raw == null) return null;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'object') return raw.score_value ?? raw.value ?? null;
  if (typeof raw === 'string' && /^\d+$/.test(raw)) return Number(raw);
  return null;
}

function npsFromRaw(raw) {
  if (raw == null) return null;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'object') return raw.score_value ?? raw.value ?? null;
  if (typeof raw === 'string' && /^\d+$/.test(raw)) return Number(raw);
  return null;
}

function commentOf(raw) {
  return (raw && typeof raw === 'object' && typeof raw.comment === 'string') ? raw.comment : '';
}

export default function SatisfinderRevision() {
  const { publicId } = useParams();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/public/revise/${encodeURIComponent(publicId)}`);
        const d = await res.json();
        if (!res.ok) { setError(d.detail || 'This link is no longer valid.'); return; }
        setBundle(d);
        setAnswers(d.answers || {});
      } catch {
        setError('We could not load your submission. Please check your link.');
      } finally { setLoading(false); }
    })();
  }, [publicId]);

  const updateAnswer = (qid, value) => setAnswers(prev => ({ ...prev, [qid]: value }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/public/revise/${encodeURIComponent(publicId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
      });
      const d = await res.json();
      if (!res.ok) { toast.error(d.detail || 'Submission failed'); return; }
      setDone(true);
    } catch {
      toast.error('Submission failed — please try again');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-[#64748B]" data-testid="revision-loading">Loading your submission…</div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-[#F8FAFC]" data-testid="revision-error">
        <div className="max-w-md w-full bg-white rounded-xl border border-[#E2E8F0] p-8 text-center">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h1 className="font-['Montserrat'] text-xl font-bold text-[#0F172A] mb-2">Link no longer valid</h1>
          <p className="text-sm text-[#64748B]">{error}</p>
        </div>
      </div>
    );
  }
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-[#F8FAFC]" data-testid="revision-thank-you">
        <div className="max-w-md w-full bg-white rounded-xl border border-[#E2E8F0] p-10 text-center space-y-4">
          <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
          <h1 className="font-['Montserrat'] text-2xl font-bold text-[#0F172A]">Thank you!</h1>
          <p className="text-sm text-[#64748B] leading-relaxed">
            Your updated FIND has been received and is now back with the satisFIND<sup>®</sup> QA team for review.
            You can close this window.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="revision-page">
      <main className="max-w-3xl mx-auto px-5 py-8 space-y-6">
        <RevisionHeader bundle={bundle} />
        {(bundle.questions || []).map(q => (
          <QuestionEditor
            key={q.question_id || q.id}
            q={q}
            value={answers[q.question_id || q.id]}
            onChange={(v) => updateAnswer(q.question_id || q.id, v)}
            branchId={bundle.branch_id}
          />
        ))}
        <div className="sticky bottom-4 bg-white rounded-xl border border-[#E2E8F0] shadow-lg p-4 flex items-center justify-between">
          <p className="text-xs text-[#64748B]">All other fields — photos, voice, etc. — stay as you originally submitted unless you change them above.</p>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-[#1A3E6F] hover:bg-[#0F2A4F] text-white"
            data-testid="revision-submit-btn"
          >
            {submitting ? 'Submitting…' : 'Submit updated FIND'}
          </Button>
        </div>
      </main>
    </div>
  );
}

function RevisionHeader({ bundle }) {
  const expiresStr = bundle.expires_at ? new Date(bundle.expires_at).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }) : '—';
  return (
    <header className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <span className="font-['Montserrat'] font-bold text-[#1A3E6F] text-xl">satisFIND<sup>®</sup></span>
        <span className="text-xs text-[#64748B] inline-flex items-center gap-1">
          <Clock className="w-3.5 h-3.5" /> Link expires {expiresStr}
        </span>
      </div>
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 space-y-3" data-testid="revision-header-card">
        <h1 className="font-['Montserrat'] text-2xl font-bold text-[#0F172A]">Update your submitted FIND</h1>
        <p className="text-sm text-[#475569] leading-relaxed">
          Hi{bundle.satisfinder_name ? ` ${bundle.satisfinder_name}` : ''}, please review your previously submitted answers for{' '}
          <span className="font-semibold">{bundle.branch_name || 'this branch'}</span> and update anything that needs correcting.
        </p>
        {bundle.revision_note && (
          <div className="bg-amber-50 border-l-4 border-amber-400 px-4 py-3 rounded-r" data-testid="revision-note">
            <p className="text-xs uppercase tracking-wider font-bold text-amber-800 mb-1">Note from satisFIND<sup>®</sup></p>
            <p className="text-sm text-amber-900 whitespace-pre-wrap">{bundle.revision_note}</p>
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm pt-1">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">Survey</p>
            <p className="text-[#0F172A]">{bundle.survey_name || '—'}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">Branch</p>
            <p className="text-[#0F172A]">{bundle.branch_name || '—'}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">Report ID</p>
            <p className="text-[#0F172A] font-mono text-xs">{bundle.public_id}</p>
          </div>
        </div>
      </div>
    </header>
  );
}

function QuestionEditor({ q, value, onChange, branchId }) {
  const qtype = getQuestionType(q);
  const qtext = getQuestionLabel(q);
  const sub = q.sub_text || q.help_text || q.subtitle || q.description || '';
  const cleanSub = sub ? cleanQuestionLabel(sub) : '';

  // Display-only blocks: no editor
  if (DISPLAY_ONLY_TYPES.has(qtype)) {
    if (['welcome', 'thank_you', 'page_break', 'confirmation'].includes(qtype)) return null;
    if (qtype === 'section_header') {
      return (
        <div className="pt-2">
          <div className="flex items-center gap-3 mb-1">
            <div className="flex-1 h-px bg-[#1A3E6F]/20" />
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1A3E6F]">Section</span>
            <div className="flex-1 h-px bg-[#1A3E6F]/20" />
          </div>
          {qtext && <h2 className="font-['Montserrat'] text-lg font-bold text-[#0F172A] text-center">{qtext}</h2>}
          {cleanSub && <p className="text-sm text-[#64748B] text-center">{cleanSub}</p>}
        </div>
      );
    }
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] p-5 space-y-3" data-testid={`revision-q-${q.question_id || q.id}`}>
      <div>
        <p className="font-semibold text-[#0F172A]">
          {qtext || <span className="text-[#94A3B8] italic">Untitled question</span>}
        </p>
        {cleanSub && <p className="text-xs text-[#64748B] mt-0.5">{cleanSub}</p>}
      </div>
      <EditorByType q={q} qtype={qtype} value={value} onChange={onChange} branchId={branchId} />
    </div>
  );
}

function EditorByType({ q, qtype, value, onChange, branchId }) {
  const qid = q.question_id || q.id;

  // Voice — show original audio + editable transcription
  if (qtype === 'voice') {
    const audioUrl = (value && typeof value === 'object' && value.audio_url) ? value.audio_url : null;
    const transcription = (value && typeof value === 'object') ? (value.transcription || '') : (typeof value === 'string' ? value : '');
    const updateTranscription = (t) => onChange({ ...(value || {}), transcription: t });
    return (
      <div className="space-y-2">
        {audioUrl && (
          <div className="flex items-center gap-2">
            <Volume2 className="w-4 h-4 text-[#D62B27]" />
            <AudioPlayer url={audioUrl} />
          </div>
        )}
        <Textarea
          value={transcription}
          onChange={(e) => updateTranscription(e.target.value)}
          rows={4}
          placeholder="What you said (you can edit this transcription)"
          data-testid={`revision-input-${qid}`}
        />
      </div>
    );
  }

  // Short text / long text
  if (['short_text', 'long_text', 'text'].includes(qtype)) {
    const txt = (typeof value === 'object' && value && 'value' in value) ? String(value.value ?? '') : (value == null ? '' : String(value));
    return qtype === 'short_text' ? (
      <Input value={txt} onChange={(e) => onChange(e.target.value)} placeholder="Your answer" data-testid={`revision-input-${qid}`} />
    ) : (
      <Textarea value={txt} onChange={(e) => onChange(e.target.value)} rows={5} placeholder="Your answer" data-testid={`revision-input-${qid}`} />
    );
  }

  // Yes/No, single choice, dropdown
  if (['yes_no', 'single_choice', 'button_choice', 'dropdown'].includes(qtype)) {
    const options = ResolveOptions(q);
    const current = selectedLabelsFromRaw(value);
    const comment = commentOf(value);
    const isCommentable = q.commentable || (current && comment);
    const onPick = (label) => {
      const next = buildChoiceAnswer(qtype, [label]);
      if (comment) next.comment = comment;
      onChange(next);
    };
    const onCommentChange = (c) => {
      const wrapper = (value && typeof value === 'object') ? { ...value, comment: c } : buildChoiceAnswer(qtype, current);
      onChange({ ...wrapper, comment: c });
    };
    return (
      <div className="space-y-3">
        {qtype === 'dropdown' ? (
          <Select value={current[0] || ''} onValueChange={onPick}>
            <SelectTrigger className="w-full" data-testid={`revision-select-${qid}`}>
              <SelectValue placeholder="Choose an option" />
            </SelectTrigger>
            <SelectContent>
              {options.map(o => <SelectItem key={o.label} value={o.label}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid={`revision-choices-${qid}`}>
            {options.map(o => {
              const active = current[0] === o.label;
              return (
                <button
                  type="button"
                  key={o.label}
                  onClick={() => onPick(o.label)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                    active ? 'bg-[#1A3E6F] text-white border-[#1A3E6F]' : 'bg-white text-[#475569] border-[#CBD5E1] hover:border-[#1A3E6F]'
                  }`}
                  data-testid={`revision-choice-${qid}-${o.label}`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        )}
        {isCommentable && (
          <Textarea
            value={comment}
            onChange={(e) => onCommentChange(e.target.value)}
            placeholder="Optional comment"
            rows={2}
            data-testid={`revision-comment-${qid}`}
          />
        )}
      </div>
    );
  }

  // Multiple choice
  if (qtype === 'multiple_choice') {
    const options = ResolveOptions(q);
    const current = new Set(selectedLabelsFromRaw(value));
    const comment = commentOf(value);
    const onToggle = (label, checked) => {
      const next = new Set(current);
      if (checked) next.add(label); else next.delete(label);
      const ans = buildChoiceAnswer('multiple_choice', Array.from(next));
      const final = comment ? { ...ans, comment } : ans;
      onChange(final);
    };
    const onCommentChange = (c) => onChange({ ...(value && typeof value === 'object' ? value : {}), comment: c });
    return (
      <div className="space-y-2" data-testid={`revision-multi-${qid}`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {options.map(o => {
            const checked = current.has(o.label);
            return (
              <label key={o.label} className="flex items-center gap-2 px-3 py-2 rounded-md border border-[#E2E8F0] cursor-pointer hover:border-[#1A3E6F]">
                <Checkbox checked={checked} onCheckedChange={(v) => onToggle(o.label, !!v)} data-testid={`revision-multi-${qid}-${o.label}`} />
                <span className="text-sm text-[#0F172A]">{o.label}</span>
              </label>
            );
          })}
        </div>
        {q.commentable && (
          <Textarea value={comment} onChange={(e) => onCommentChange(e.target.value)} placeholder="Optional comment" rows={2} />
        )}
      </div>
    );
  }

  // Star rating
  if (qtype === 'star_rating') {
    const max = q.scale_max || 5;
    const current = ratingFromRaw(value) || 0;
    const comment = commentOf(value);
    const setRating = (n) => {
      const wrapper = (value && typeof value === 'object') ? { ...value, score_value: n, value: n } : { score_value: n, value: n };
      if (comment) wrapper.comment = comment;
      onChange(wrapper);
    };
    return (
      <div className="space-y-2" data-testid={`revision-star-${qid}`}>
        <div className="flex items-center gap-1">
          {Array.from({ length: max }, (_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setRating(i + 1)}
              className="p-1"
              data-testid={`revision-star-${qid}-${i + 1}`}
            >
              <Star className={`w-7 h-7 ${i < current ? 'fill-[#F1D511] text-[#F1D511]' : 'text-[#CBD5E1]'}`} />
            </button>
          ))}
          <span className="text-sm text-[#475569] ml-2 font-medium">{current}/{max}</span>
        </div>
        {q.commentable && (
          <Textarea
            value={comment}
            onChange={(e) => onChange({ ...(value && typeof value === 'object' ? value : { score_value: current, value: current }), comment: e.target.value })}
            placeholder="Optional comment"
            rows={2}
          />
        )}
      </div>
    );
  }

  // Rating scale
  if (qtype === 'rating_scale') {
    const max = q.scale_max || 5;
    const current = ratingFromRaw(value);
    const comment = commentOf(value);
    return (
      <div className="space-y-2" data-testid={`revision-rating-${qid}`}>
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: max }, (_, i) => {
            const n = i + 1;
            const active = current === n;
            return (
              <button
                key={n}
                type="button"
                onClick={() => onChange({ ...(value && typeof value === 'object' ? value : {}), score_value: n, value: n })}
                className={`w-10 h-10 rounded-md font-bold text-sm border ${
                  active ? 'bg-[#1A3E6F] text-white border-[#1A3E6F]' : 'bg-white text-[#475569] border-[#CBD5E1] hover:border-[#1A3E6F]'
                }`}
                data-testid={`revision-rating-${qid}-${n}`}
              >{n}</button>
            );
          })}
        </div>
        {q.commentable && (
          <Textarea
            value={comment}
            onChange={(e) => onChange({ ...(value && typeof value === 'object' ? value : { score_value: current, value: current }), comment: e.target.value })}
            placeholder="Optional comment"
            rows={2}
          />
        )}
      </div>
    );
  }

  // NPS
  if (qtype === 'nps') {
    const current = npsFromRaw(value);
    const comment = commentOf(value);
    return (
      <div className="space-y-2" data-testid={`revision-nps-${qid}`}>
        <div className="flex flex-wrap gap-1">
          {Array.from({ length: 11 }, (_, i) => {
            const active = current === i;
            const tone = i >= 9 ? 'border-emerald-300' : i >= 7 ? 'border-amber-300' : 'border-rose-300';
            return (
              <button
                key={i}
                type="button"
                onClick={() => onChange({ ...(value && typeof value === 'object' ? value : {}), score_value: i, value: i })}
                className={`w-9 h-9 rounded-md font-bold text-sm border-2 ${tone} ${
                  active ? (i >= 9 ? 'bg-emerald-500 text-white border-emerald-500' : i >= 7 ? 'bg-amber-500 text-white border-amber-500' : 'bg-rose-500 text-white border-rose-500') : 'bg-white text-[#475569] hover:bg-[#F1F5F9]'
                }`}
                data-testid={`revision-nps-${qid}-${i}`}
              >{i}</button>
            );
          })}
        </div>
        {q.commentable && (
          <Textarea
            value={comment}
            onChange={(e) => onChange({ ...(value && typeof value === 'object' ? value : { score_value: current, value: current }), comment: e.target.value })}
            placeholder="Why?"
            rows={2}
          />
        )}
      </div>
    );
  }

  // Number / number_input
  if (['number', 'number_input'].includes(qtype)) {
    const txt = (typeof value === 'object' && value) ? String(value.value ?? '') : (value == null ? '' : String(value));
    return (
      <div className="flex items-center gap-2">
        <Hash className="w-4 h-4 text-[#94A3B8]" />
        <Input
          type="number"
          value={txt}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          placeholder="Enter a number"
          className="max-w-xs"
          data-testid={`revision-input-${qid}`}
        />
      </div>
    );
  }

  // Date / Time
  if (qtype === 'date' || qtype === 'time') {
    const txt = value == null ? '' : String(value);
    return <Input type={qtype} value={txt} onChange={(e) => onChange(e.target.value)} className="max-w-xs" data-testid={`revision-input-${qid}`} />;
  }

  // File upload — existing photos with Remove + Replace + Add more
  if (qtype === 'file_upload') {
    return <FileUploadEditor q={q} value={value} onChange={onChange} branchId={branchId} />;
  }

  // Fallback — render as plain text
  const fallback = value == null ? '' : (typeof value === 'object' ? JSON.stringify(value) : String(value));
  return (
    <Textarea value={fallback} onChange={(e) => onChange(e.target.value)} rows={3} placeholder="Your answer" data-testid={`revision-input-${qid}`} />
  );
}

function FileUploadEditor({ q, value, onChange, branchId }) {
  const current = Array.isArray(value) ? value : (value ? [value] : []);
  const allowed = Array.isArray(q.allowed_types) && q.allowed_types.length > 0 ? q.allowed_types : ['image'];
  const maxFiles = Math.max(1, Math.min(20, Number(q.max_files) || 1));
  const maxSizeMb = Math.max(1, Math.min(50, Number(q.max_file_size_mb) || 5));
  const fileInputRef = useRef(null);
  const replaceIdxRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // In-flight / failed uploads (per-file progress + retry) for multi-add.
  const [transient, setTransient] = useState([]);
  // Live mirror of the latest value so sequential uploads never clobber
  // earlier files (or captions edited mid-batch).
  const valueRef = useRef(value);
  valueRef.current = value;

  const acceptAttr = [
    allowed.includes('image') ? 'image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif' : '',
    allowed.includes('pdf') ? 'application/pdf' : '',
  ].filter(Boolean).join(',') || (allowed.includes('image') ? 'image/*' : '*/*');
  const pendingCount = transient.filter(t => t._pending).length;
  const activeCount = current.length + pendingCount;
  const remainingSlots = Math.max(0, maxFiles - activeCount);

  const allowedMimes = useMemo(() => {
    const m = new Set();
    if (allowed.includes('image')) {
      ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'].forEach(x => m.add(x));
    }
    if (allowed.includes('pdf')) m.add('application/pdf');
    return m;
  }, [allowed]);

  const doUpload = async (file) => {
    if (!file) return null;
    if (allowedMimes.size > 0 && !allowedMimes.has(file.type.toLowerCase())) {
      toast.error(`Only ${allowed.join(' / ')} files are allowed`);
      return null;
    }
    if (file.size > maxSizeMb * 1024 * 1024) {
      toast.error(`File size must be under ${maxSizeMb}MB`);
      return null;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('branch_id', branchId || '');
      form.append('question_id', q.question_id || q.id);
      form.append('allowed_types', allowed.join(','));
      form.append('max_file_size_mb', String(maxSizeMb));
      const res = await fetch(`${API}/public/survey/upload-file`, { method: 'POST', body: form });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Upload failed');
        return null;
      }
      const data = await res.json();
      return { url: data.file_url, comment: '', name: file.name, type: file.type };
    } catch {
      toast.error('Upload failed');
      return null;
    } finally { setUploading(false); }
  };

  const onPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const idx = replaceIdxRef.current;
    replaceIdxRef.current = null;
    const newEntry = await doUpload(file);
    if (!newEntry) return;
    if (typeof idx === 'number') {
      const next = current.map((f, i) => (i === idx ? { ...newEntry, comment: f.comment || '' } : f));
      onChange(next);
    } else {
      if (current.length >= maxFiles) {
        toast.error(`You can only attach up to ${maxFiles} file${maxFiles === 1 ? '' : 's'}`);
        return;
      }
      onChange([...current, newEntry]);
    }
  };

  // Throwing variant used by the multi-add flow (per-file transient tiles).
  const uploadOne = async (file) => {
    const form = new FormData();
    form.append('file', file);
    form.append('branch_id', branchId || '');
    form.append('question_id', q.question_id || q.id);
    form.append('allowed_types', allowed.join(','));
    form.append('max_file_size_mb', String(maxSizeMb));
    const res = await fetch(`${API}/public/survey/upload-file`, { method: 'POST', body: form });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Upload failed');
    }
    const data = await res.json();
    return { url: data.file_url, comment: '', name: file.name, type: file.type };
  };

  const onPickMulti = async (files) => {
    if (!files.length) return;
    if (files.length > remainingSlots) {
      toast.error(`You can upload ${remainingSlots} more file${remainingSlots === 1 ? '' : 's'}.`);
      return;
    }
    let acc = Array.isArray(value) ? [...value] : (value ? [value] : []);
    for (const file of files) {
      if (allowedMimes.size > 0 && !allowedMimes.has((file.type || '').toLowerCase())) {
        toast.error(`${file.name}: only ${allowed.join(' / ')} files are allowed`);
        continue;
      }
      if (file.size > maxSizeMb * 1024 * 1024) {
        toast.error(`${file.name}: must be under ${maxSizeMb}MB`);
        continue;
      }
      const key = `${file.name}-${file.size}-${Date.now()}-${Math.random()}`;
      setTransient(prev => [...prev, { _key: key, _pending: true, _file: file, name: file.name, size: file.size }]);
      try {
        const entry = await uploadOne(file);
        setTransient(prev => prev.filter(t => t._key !== key));
        acc = [...(Array.isArray(valueRef.current) ? valueRef.current : (valueRef.current ? [valueRef.current] : [])), entry];
        onChange(acc);
      } catch (err) {
        setTransient(prev => prev.map(t => t._key === key ? { ...t, _pending: false, _error: err.message || 'Upload failed' } : t));
      }
    }
  };

  const retryTransient = async (key) => {
    const target = transient.find(t => t._key === key);
    if (!target) return;
    setTransient(prev => prev.map(t => t._key === key ? { ...t, _pending: true, _error: undefined } : t));
    try {
      const entry = await uploadOne(target._file);
      setTransient(prev => prev.filter(t => t._key !== key));
      onChange([...(Array.isArray(valueRef.current) ? valueRef.current : (valueRef.current ? [valueRef.current] : [])), entry]);
    } catch (err) {
      setTransient(prev => prev.map(t => t._key === key ? { ...t, _pending: false, _error: err.message || 'Upload failed' } : t));
    }
  };
  const removeTransient = (key) => setTransient(prev => prev.filter(t => t._key !== key));

  const triggerReplace = (idx) => {
    replaceIdxRef.current = idx;
    fileInputRef.current?.click();
  };
  const removeAt = (idx) => onChange(current.filter((_, i) => i !== idx));
  const updateCommentAt = (idx, comment) => onChange(current.map((f, i) => i === idx ? { ...f, comment } : f));

  return (
    <div className="space-y-3" data-testid={`revision-files-${q.question_id || q.id}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptAttr}
        className="hidden"
        onChange={onPicked}
      />
      {current.length === 0 && transient.length === 0 && (
        <p className="text-xs text-[#94A3B8] italic">No file attached yet.</p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {current.map((f, i) => {
          const url = toAbsUrl(f.url);
          const isImg = (f.type || '').startsWith('image/') || /\.(jpe?g|png|webp|gif)$/i.test(url);
          return (
            <div key={i} className="border border-[#E2E8F0] rounded-md p-2 space-y-2">
              {isImg ? (
                <a href={url} target="_blank" rel="noreferrer">
                  <img src={url} alt={f.name || `file-${i + 1}`} className="w-full h-32 object-cover rounded" />
                </a>
              ) : (
                <a href={url} target="_blank" rel="noreferrer" className="block h-32 flex items-center justify-center bg-[#F8FAFC] rounded text-[#64748B]">
                  <FileText className="w-8 h-8" />
                </a>
              )}
              <Input
                value={f.comment || ''}
                onChange={(e) => updateCommentAt(i, e.target.value)}
                placeholder="Add a caption"
                className="text-xs"
                data-testid={`revision-file-comment-${q.question_id || q.id}-${i}`}
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => triggerReplace(i)}
                  disabled={uploading || pendingCount > 0}
                  className="flex-1"
                  data-testid={`revision-file-replace-${q.question_id || q.id}-${i}`}
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Replace
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => removeAt(i)}
                  disabled={uploading || pendingCount > 0}
                  className="text-rose-600 border-rose-200 hover:bg-rose-50"
                  data-testid={`revision-file-remove-${q.question_id || q.id}-${i}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
        {transient.map((t) => (
          <div
            key={t._key}
            className={`border rounded-md p-2 flex flex-col items-center justify-center text-center ${t._error ? 'border-rose-300 bg-rose-50' : 'border-blue-200 bg-blue-50'}`}
            style={{ minHeight: 150 }}
            data-testid={`revision-file-transient-${q.question_id || q.id}-${t._key}`}
          >
            {t._pending ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin text-[#1A3E6F]" />
                <p className="text-[11px] text-[#1A3E6F] mt-1">Uploading…</p>
              </>
            ) : (
              <>
                <AlertTriangle className="w-6 h-6 text-rose-600" />
                <p className="text-[11px] text-rose-700 mt-1">Upload failed</p>
                <div className="flex gap-2 mt-1">
                  <button type="button" onClick={() => retryTransient(t._key)} className="text-[11px] text-[#1A3E6F] underline font-semibold" data-testid={`revision-file-retry-${q.question_id || q.id}-${t._key}`}>Retry</button>
                  <button type="button" onClick={() => removeTransient(t._key)} className="text-[11px] text-[#64748B] underline">Dismiss</button>
                </div>
              </>
            )}
            <p className="text-[10px] text-[#94A3B8] truncate w-full px-1 mt-1" title={t.name}>{t.name}</p>
          </div>
        ))}
      </div>
      {remainingSlots > 0 ? (
        <FileDropzone
          onFiles={onPickMulti}
          accept={acceptAttr}
          multiple={maxFiles > 1}
          disabled={pendingCount > 0}
          idleLabel={activeCount === 0 ? 'Add a file' : 'Add another file'}
          subLabel={maxFiles > 1 ? `(${activeCount}/${maxFiles})` : ''}
          inputTestId={`revision-file-input-${q.question_id || q.id}`}
          dropTestId={`revision-file-add-${q.question_id || q.id}`}
        />
      ) : (
        <p className="text-[11px] text-[#94A3B8] italic" data-testid={`revision-file-limit-${q.question_id || q.id}`}>Maximum {maxFiles} file{maxFiles === 1 ? '' : 's'} attached.</p>
      )}
      {allowed.includes('image') && !allowed.includes('pdf') && (
        <p className="text-[11px] text-[#94A3B8]">JPG / PNG / WEBP, up to {maxSizeMb}MB per file. Max {maxFiles} file{maxFiles === 1 ? '' : 's'}.</p>
      )}
    </div>
  );
}
